import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getDatabase, ref, onValue, set, update, get } from 'firebase/database'; // Import 'set', 'update' und 'get' hier
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import AddWidgets from './AddWidgets';
import PagePreview from './PagePreview';
import WidgetEdit from './WidgetEdit';
import PopupManager from './PopupManager';
import PopupStylePreview from './PopupStylePreview';
import './PageSettings.css'; // Importieren Sie Ihre CSS-Datei
import PageNav from './PageNav';
import { useUserProducts } from './hooks/useProductData';
import { applyPageSettingsToManualWidget, applySpaceSettingsToManualWidget } from './utils/widgetStylingUtils';
import { useFont } from '../Design/FontContext';
import { useHistory } from '../hooks/useHistory'; // ✅ NEW: History Hook für Undo/Redo

function PageSettings() {
    const { pageId, userId, spaceId, pageName: routePageName } = useParams();
    const db = getDatabase();
    const auth = getAuth();
    const fonts = useFont(); // ✅ Brand Fonts aus FontContext
    const [loading, setLoading] = useState(true);
    const [pageData, setPageData] = useState({ widgets: [] });
    const [activeWidgetId, setActiveWidgetId] = useState(null);
    const [allPages, setAllPages] = useState([]); // Neuer State für alle Seiten
    const [showPopup, setShowPopup] = useState(false);
    const [colors, setColors] = useState([]); // Neuer State für Farbdaten
    const [colorAssignments, setColorAssignments] = useState({}); // ✅ NEW: Color Assignments State
    const [spaceFonts, setSpaceFonts] = useState({}); // ✅ NEW: Space Fonts State
    
    // 🔄 NEW: History Management für Undo/Redo
    const history = useHistory(pageData.widgets || []);
    const historyEnabledRef = useRef(false); // Flag to enable history after initial load
    const [isWidgetEditCollapsed, setIsWidgetEditCollapsed] = useState(true); // State für rechten Toggle - DEFAULT: Geschlossen
    const [isAddWidgetsCollapsed, setIsAddWidgetsCollapsed] = useState(false); // State für linken Toggle
    const [activeRightTab, setActiveRightTab] = useState('widget'); // 'widget' or 'popup'
    const [popupStyling, setPopupStyling] = useState({});
    const [isMobileView, setIsMobileView] = useState(false); // NEW: Mobile View Toggle State
    const [isGlobalSettingsOpen, setIsGlobalSettingsOpen] = useState(false); // Track GlobalSettings Modal state
    const [isNavigationEnabled, setIsNavigationEnabled] = useState(false); // NEW: Navigation Toggle State
    const [positionsAlreadyFixed, setPositionsAlreadyFixed] = useState(false); // 🔧 Track if position fix was already applied
    
    // Check if current widget can trigger popups
    const canTriggerPopup = () => {
        if (!selectedWidget) {
            return false;
        }
        
        // Check for ProductInfoWidget with free digital product
        if (selectedWidget.type === 'ProductInfoWidget') {
            const productId = selectedWidget.buttonProductId || selectedWidget.productId;
            
            if (productId) {
                const product = products.find(p => p.id === productId);
                
                if (product) {
                    const isFree = product.price === 0 || product.price === '0';
                    const isDigital = product.type === 'digital-product' || product.type === 'online-course' || product.type === 'digital';
                    
                    if (isFree && isDigital) {
                        return true;
                    }
                }
            }
        }
        
        // Check for Button widgets with productForm action
        if (['BigButton', 'EliteButton', 'MegaButton'].includes(selectedWidget.type)) {
            if (selectedWidget.actionType === 'productForm' && selectedWidget.productId) {
                const product = products.find(p => p.id === selectedWidget.productId);
                
                if (product && (product.price === 0 || product.price === '0')) {
                    return true;
                }
            }
        }
        
        return false;
    };
    
    // Product Integration State
    const { products } = useUserProducts();
    const [selectedProductId, setSelectedProductId] = useState('');

    function generateUniqueId() {
        return Math.random().toString(36).substr(2, 9);
    }
    
    // 🔄 NEW: Sync history.state with pageData.widgets
    useEffect(() => {
        if (historyEnabledRef.current && history.state) {
            console.log('📚 [HISTORY] Syncing history state to pageData');
            setPageData(prev => ({
                ...prev,
                widgets: history.state
            }));
        }
    }, [history.state]);
    
    // 🔄 NEW: Enable history after initial page load (avoid pushing initial state)
    useEffect(() => {
        if (!loading && pageData.widgets && pageData.widgets.length >= 0) {
            // Wait a bit before enabling history tracking
            const timer = setTimeout(() => {
                historyEnabledRef.current = true;
                console.log('✅ [HISTORY] History tracking enabled');
            }, 1000);
            
            return () => clearTimeout(timer);
        }
    }, [loading, pageData.widgets]);
    
    // 🔄 NEW: Push to history when widgets change (but not from undo/redo)
    const updateWidgetsWithHistory = (newWidgets, options = {}) => {
        const { immediate = false, skipHistory = false } = options;
        
        console.log('🔄 [HISTORY] updateWidgetsWithHistory called:', { 
            widgetCount: newWidgets.length, 
            immediate, 
            skipHistory,
            historyEnabled: historyEnabledRef.current 
        });
        
        // Update pageData
        setPageData(prev => ({
            ...prev,
            widgets: newWidgets
        }));
        
        // Push to history (if enabled and not skipped)
        if (historyEnabledRef.current && !skipHistory) {
            history.pushState(newWidgets, { immediate });
        }
    };
    
    // 🔄 NEW: Undo Handler
    const handleUndo = () => {
        if (history.canUndo) {
            console.log('⏪ [PAGE SETTINGS] Triggering Undo');
            history.undo();
        }
    };
    
    // 🔄 NEW: Redo Handler
    const handleRedo = () => {
        if (history.canRedo) {
            console.log('⏩ [PAGE SETTINGS] Triggering Redo');
            history.redo();
        }
    };

    // ❌ REMOVED: Old Page Colors loading (now using Space Colors)
    // Page Colors werden durch Space Colors aus designSettings ersetzt
    // useEffect(() => {
    //     const currentPageId = userId && spaceId && routePageName ? `${userId}/${spaceId}/${routePageName}` : 
    //                           userId && routePageName ? `${userId}/${routePageName}` : pageId;
    //     const colorsRef = ref(db, `/pages/${currentPageId}/colors`);
    //     onValue(colorsRef, (snapshot) => {
    //         const colorsData = snapshot.val() || [];
    //         setColors(colorsData); // Speichert die abgerufenen Farbdaten im State
    //     });
    // }, [db, pageId, userId, routePageName]); // Abhängigkeiten für useEffect
    
    // ✅ NEW: Load Space Fonts & Color Assignments for Templates
    useEffect(() => {
        const loadSpaceSettings = async () => {
            if (!userId || !spaceId) return;
            
            try {
                const spaceRef = ref(db, `/users/${userId}/spaces/${spaceId}`);
                const snapshot = await get(spaceRef);
                
                if (snapshot.exists()) {
                    const spaceData = snapshot.val();
                    
                    // Load Fonts
                    const loadedFonts = {
                        globalBigFont: spaceData.designSettings?.globalBigFont || spaceData.globalBigFont || 'Voyage',
                        globalSmallFont: spaceData.designSettings?.globalSmallFont || spaceData.globalSmallFont || 'Jost'
                    };
                    
                    // ✅ NEW: Load Color Assignments
                    const loadedColorAssignments = spaceData.designSettings?.colorAssignments || {
                        background: 'color5',
                        primaryText: 'color2',
                        secondaryText: 'color3',
                        buttons: 'color1',
                        buttonText: 'color5',
                        accents: 'color1'
                    };
                    
                    // ✅ CRITICAL FIX: Load Space Colors (color1, color2, etc.)
                    const loadedColors = spaceData.designSettings?.colors || {
                        color1: '#3182CE',
                        color2: '#2D3748',
                        color3: '#4A5568',
                        color4: '#F7FAFC',
                        color5: '#FFFFFF'
                    };
                    
                    console.log('🔤 [PAGE SETTINGS] Space Settings loaded:', {
                        userId,
                        spaceId,
                        loadedFonts,
                        loadedColorAssignments,
                        loadedColors,
                        hasDesignSettings: !!spaceData.designSettings
                    });
                    
                    setSpaceFonts(loadedFonts);
                    setColorAssignments(loadedColorAssignments);
                    setColors(loadedColors); // ✅ CRITICAL: Set the actual color values!
                }
            } catch (error) {
                console.error('❌ Error loading space settings:', error);
            }
        };
        
        loadSpaceSettings();
    }, [userId, spaceId, db]);

    // Initialize selectedProductId when pageData loads
    useEffect(() => {
        if (pageData.connectedProductId) {
            setSelectedProductId(pageData.connectedProductId);
        }
    }, [pageData.connectedProductId]);

    useEffect(() => {
        // Stellen Sie sicher, dass ein Widget ausgewählt ist, wenn Widgets vorhanden sind
        if (pageData.widgets.length > 0 && activeWidgetId === null) {
            setActiveWidgetId(pageData.widgets[0].id);
        }
        
        // Wechsle zurück zu Widget Edit Tab, wenn das aktuelle Widget keine Popups triggern kann
        if (activeRightTab === 'popup' && !canTriggerPopup()) {
            setActiveRightTab('widget');
        }
    }, [pageData.widgets, activeWidgetId, activeRightTab, canTriggerPopup]); // Fügen Sie activeWidgetId hinzu, um sicherzustellen, dass dies nur läuft, wenn kein Widget aktiv ist

    const updateWidgetsInDatabase = (widgetsArray) => {
        // Debug Route-Parameter
        console.log('🔍 updateWidgetsInDatabase: Route params:', { pageId, userId, spaceId, routePageName });
        
        // Bestimme Page-Referenz basierend auf Route-Typ
        const currentPageId = userId && spaceId && routePageName ? `${userId}/${spaceId}/${routePageName}` : 
                              userId && routePageName ? `${userId}/${routePageName}` : pageId;
        
        console.log('🔍 updateWidgetsInDatabase: Calculated currentPageId:', currentPageId);
        
        if (!currentPageId || currentPageId === 'undefined') {
            console.error('❌ Invalid currentPageId, cannot update widgets');
            return;
        }
        
        // Update widgets AND updatedAt timestamp for change detection
        const updates = {};
        updates[`/pages/${currentPageId}/widgets`] = widgetsArray;
        updates[`/pages/${currentPageId}/updatedAt`] = new Date().toISOString();
        
        const dbRef = ref(db);
        update(dbRef, updates)
            .then(() => console.log("✅ Widgets and timestamp updated in database:", currentPageId))
            .catch((error) => console.error("❌ Error updating widgets:", error));
    };

    // Hilfsfunktion, um die Widgets in der Firebase-Datenbank zu aktualisieren
    const handleUpdateWidget = (updatedWidget, options = {}) => {
        let { immediate = null } = options;
        
        // 🧠 SMART DETECTION: Wenn immediate nicht explizit gesetzt ist, automatisch erkennen
        if (immediate === null) {
            const oldWidget = pageData.widgets.find(w => w.id === updatedWidget.id);
            
            if (oldWidget) {
                // Check for structural changes that should be immediate:
                // 1. Container count changed (add/delete container)
                const oldContainerCount = oldWidget.containerSections?.length || 0;
                const newContainerCount = updatedWidget.containerSections?.length || 0;
                
                // 2. Content elements count changed (add/delete element)
                const oldElementCount = oldWidget.contentElements?.length || 0;
                const newElementCount = updatedWidget.contentElements?.length || 0;
                
                // 3. Widget type/structure changed
                const structureChanged = oldWidget.type !== updatedWidget.type;
                
                // If any structural change: immediate = true, otherwise debounce
                immediate = (
                    oldContainerCount !== newContainerCount || 
                    oldElementCount !== newElementCount ||
                    structureChanged
                );
                
                if (immediate) {
                    console.log('🔍 [SMART] Detected structural change:', {
                        containerChange: oldContainerCount !== newContainerCount,
                        elementChange: oldElementCount !== newElementCount,
                        structureChange: structureChanged
                    });
                }
            } else {
                // New widget? Shouldn't happen here, but default to immediate
                immediate = true;
            }
        }
        
        const updatedWidgets = pageData.widgets.map(widget =>
            widget.id === updatedWidget.id ? updatedWidget : widget
        );
        
        // ✅ HISTORY: Use updateWidgetsWithHistory for Undo/Redo support
        // - immediate: true for structural changes (delete container, reorder, etc.)
        // - immediate: false for text edits (debounced for 1 second)
        updateWidgetsWithHistory(updatedWidgets, { immediate });
        updateWidgetsInDatabase(updatedWidgets);
        
        if (immediate) {
            console.log(`⚡ [HISTORY] Widget updated (immediate) - can undo now!`);
        } else {
            console.log(`✏️ [HISTORY] Widget updated (debounced) - will save after 1s`);
        }
    };

    // Funktion zum Neuordnen der Widgets
    const onReorderWidgets = (startIndex, endIndex) => {
        if (!pageData.widgets || !Array.isArray(pageData.widgets)) {
            // Wenn pageData.widgets nicht existiert oder kein Array ist, setzen Sie es auf ein leeres Array
            setPageData({ ...pageData, widgets: [] });
            console.log("pageData.widgets existiert nicht oder ist kein Array. Wird auf leeres Array gesetzt.");
            return;
        }

        // Widgets neu anordnen
        const updatedWidgets = [...pageData.widgets];
        const [movedWidget] = updatedWidgets.splice(startIndex, 1);
        updatedWidgets.splice(endIndex, 0, movedWidget);

        // ✅ FIX: Aktualisiere position-Property für jedes Widget basierend auf neuer Array-Position
        const widgetsWithUpdatedPositions = updatedWidgets.map((widget, index) => ({
            ...widget,
            position: index
        }));

        // ✅ HISTORY: Use updateWidgetsWithHistory for Undo/Redo support
        updateWidgetsWithHistory(widgetsWithUpdatedPositions, { immediate: true });

        // Aktualisiere die Datenbank mit timestamp
        const currentPageId = userId && spaceId && routePageName ? `${userId}/${spaceId}/${routePageName}` : 
                              userId && routePageName ? `${userId}/${routePageName}` : pageId;
        
        const updatedPageDataWithTimestamp = {
            ...pageData,
            widgets: widgetsWithUpdatedPositions,
            updatedAt: new Date().toISOString()
        };
        
        const pageRef = ref(db, `/pages/${currentPageId}`);
        set(pageRef, updatedPageDataWithTimestamp)
            .then(() => {
                console.log("🔄 [HISTORY] Widgets reordered - can undo");
            })
            .catch((error) => {
                console.error("Fehler beim Aktualisieren der Datenbank:", error);
            });

        console.log(`Widgets neu angeordnet. Start-Index: ${startIndex}, End-Index: ${endIndex}`);
    };

    const toggleWidgetActivation = (widgetId) => {
        setActiveWidgetId(prevActiveWidgetId => {
            // Wenn die aktuelle ID gleich der neuen ist, setze zuerst auf null
            if (prevActiveWidgetId === widgetId) {
                setTimeout(() => {
                    setActiveWidgetId(widgetId);
                }, 0);
                return null;
            }
            // Ansonsten setze die neue ID
            return widgetId;
        });
    };

    const selectedWidget = pageData.widgets.find(widget => widget.id === activeWidgetId);

    const handleAddWidget = async (newWidget) => {
        console.log(`🎨 [MANUAL-WIDGET] Adding widget manually: ${newWidget.type}`);
        
        // 🔍 DEBUG: Check containerSections FONTS BEFORE styling
        if (newWidget.containerSections) {
            console.log('🔍 [BEFORE PAGE SETTINGS] Container fonts:', newWidget.containerSections.map((c, i) => ({
                containerIndex: i,
                elements: c.contentElements?.map(el => ({
                    type: el.type,
                    fontFamily: el.fontFamily,
                    contentPreview: el.content?.substring(0, 50)
                }))
            })));
        }
        
        let styledWidget = { 
            ...newWidget, 
            id: generateUniqueId(),
            // Wenn es ein Product Widget ist, füge die connectedProductId hinzu
            ...(newWidget.cat1 === 'Product' && selectedProductId ? { productId: selectedProductId } : {})
        };

        // 🎨 PAGE SETTINGS ANWENDEN (Color Assignments aus Page)
        if (userId && spaceId && routePageName) {
            try {
                console.log(`🎨 [MANUAL-WIDGET] Applying page settings for page: ${routePageName}`);
                styledWidget = await applyPageSettingsToManualWidget(styledWidget, userId, spaceId, routePageName);
                console.log(`✅ [MANUAL-WIDGET] Page settings applied successfully`);
                
                // 🔍 DEBUG: Check containerSections FONTS AFTER styling
                if (styledWidget.containerSections) {
                    console.log('🔍 [AFTER PAGE SETTINGS] Container fonts:', styledWidget.containerSections.map((c, i) => ({
                        containerIndex: i,
                        elements: c.contentElements?.map(el => ({
                            type: el.type,
                            fontFamily: el.fontFamily,
                            contentPreview: el.content?.substring(0, 50)
                        }))
                    })));
                }
            } catch (error) {
                console.error('❌ [MANUAL-WIDGET] Error applying page settings:', error);
                // Fallback: Versuche Space Settings
                try {
                    console.log(`🔄 [MANUAL-WIDGET] Fallback to space settings`);
                    styledWidget = await applySpaceSettingsToManualWidget(styledWidget, userId, spaceId);
                    console.log(`✅ [MANUAL-WIDGET] Space settings applied as fallback`);
                } catch (fallbackError) {
                    console.error('❌ [MANUAL-WIDGET] Error applying fallback settings:', fallbackError);
                    // Widget ohne Styling verwenden
                }
            }
        } else {
            console.log(`⚠️ [MANUAL-WIDGET] Missing parameters for page settings, using defaults`);
        }

        // ✅ FIX: Füge position-Property hinzu (neues Widget bekommt höchsten Index)
        const currentWidgets = pageData.widgets || [];
        const newPosition = currentWidgets.length;
        const widgetWithPosition = { ...styledWidget, position: newPosition };
        
        const updatedWidgets = [...currentWidgets, widgetWithPosition];
        
        // ✅ HISTORY: Use updateWidgetsWithHistory for Undo/Redo support
        updateWidgetsWithHistory(updatedWidgets, { immediate: true });
        setActiveWidgetId(widgetWithPosition.id);

        // Update in der Firebase-Datenbank with timestamp
        const currentPageId = userId && spaceId && routePageName ? `${userId}/${spaceId}/${routePageName}` : 
                              userId && routePageName ? `${userId}/${routePageName}` : pageId;
        
        const updatedPageDataWithTimestamp = {
            ...pageData,
            widgets: updatedWidgets,
            updatedAt: new Date().toISOString()
        };
        
        const pageRef = ref(db, `/pages/${currentPageId}`);
        set(pageRef, updatedPageDataWithTimestamp);

        // Popup anzeigen
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000); // Popup nach 3 Sekunden ausblenden
        
        console.log('➕ [HISTORY] Widget added - can undo:', widgetWithPosition.type);
    };

    // Product Selection Handler
    const handleProductSelection = (productId) => {
        setSelectedProductId(productId);
        
        // Update page data with connected product
        const updatedPageData = { 
            ...pageData, 
            connectedProductId: productId 
        };
        setPageData(updatedPageData);

        // Save to database with timestamp
        const currentPageId = userId && spaceId && routePageName ? `${userId}/${spaceId}/${routePageName}` : 
                              userId && routePageName ? `${userId}/${routePageName}` : pageId;
        
        const updatedPageDataWithTimestamp = {
            ...updatedPageData,
            updatedAt: new Date().toISOString()
        };
        
        const pageRef = ref(db, `/pages/${currentPageId}`);
        set(pageRef, updatedPageDataWithTimestamp)
            .then(() => {
                console.log("Product connection saved successfully.");
            })
            .catch((error) => {
                console.error("Error saving product connection:", error);
            });
    };

    // 📋 Widget Duplicate Handler
    const handleDuplicateWidget = (widgetId) => {
        console.log('📋 [DUPLICATE] Duplicating widget:', widgetId);
        
        const widgetToDuplicate = pageData.widgets?.find(w => w.id === widgetId);
        if (!widgetToDuplicate) {
            console.error('❌ Widget not found for duplication:', widgetId);
            return;
        }
        
        // Deep clone the widget with a new ID
        const duplicatedWidget = {
            ...JSON.parse(JSON.stringify(widgetToDuplicate)), // Deep clone
            id: generateUniqueId()
        };
        
        console.log('✅ [DUPLICATE] Widget cloned with new ID:', duplicatedWidget.id);
        
        // Find the index of the original widget
        const originalIndex = pageData.widgets.findIndex(w => w.id === widgetId);
        
        // Insert duplicated widget right after the original
        const tempWidgets = [...(pageData.widgets || [])];
        tempWidgets.splice(originalIndex + 1, 0, duplicatedWidget);
        
        // ✅ FIX: Aktualisiere position-Property für alle Widgets
        const updatedWidgets = tempWidgets.map((widget, index) => ({
            ...widget,
            position: index
        }));
        
        // ✅ HISTORY: Use updateWidgetsWithHistory for Undo/Redo support
        updateWidgetsWithHistory(updatedWidgets, { immediate: true });
        setActiveWidgetId(duplicatedWidget.id); // Select the new widget
        
        // Update in Firebase with timestamp
        const currentPageId = userId && spaceId && routePageName ? `${userId}/${spaceId}/${routePageName}` : 
                              userId && routePageName ? `${userId}/${routePageName}` : pageId;
        
        const updatedPageDataWithTimestamp = {
            ...pageData,
            widgets: updatedWidgets,
            updatedAt: new Date().toISOString()
        };
        
        const pageRef = ref(db, `/pages/${currentPageId}`);
        set(pageRef, updatedPageDataWithTimestamp);
        
        console.log('💾 [DUPLICATE] Widget duplicated - can undo');
        
        // Show popup
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
    };


    const handleRemoveWidget = (widgetId) => {
        // Aktualisiere das pageData-Objekt, indem das ausgewählte Widget entfernt wird
        const filteredWidgets = pageData.widgets.filter(widget => widget.id !== widgetId);
        
        // ✅ FIX: Aktualisiere position-Property für alle verbleibenden Widgets
        const updatedWidgets = filteredWidgets.map((widget, index) => ({
            ...widget,
            position: index
        }));
        
        // ✅ HISTORY: Use updateWidgetsWithHistory for Undo/Redo support
        updateWidgetsWithHistory(updatedWidgets, { immediate: true });
        setActiveWidgetId(null);

        const currentPageId = userId && spaceId && routePageName ? `${userId}/${spaceId}/${routePageName}` : 
                              userId && routePageName ? `${userId}/${routePageName}` : pageId;
        
        const updatedPageDataWithTimestamp = {
            ...pageData,
            widgets: updatedWidgets,
            updatedAt: new Date().toISOString()
        };
        
        const pageRef = ref(db, `/pages/${currentPageId}`);
        set(pageRef, updatedPageDataWithTimestamp);
        
        console.log('🗑️ [HISTORY] Widget removed - can undo:', widgetId);
    };

    // Toggle-Funktion für Widget-Edit Panel (rechts)
    const toggleWidgetEdit = () => {
        setIsWidgetEditCollapsed(!isWidgetEditCollapsed);
    };

    // Toggle-Funktion für Add-Widgets Panel (links)
    const toggleAddWidgets = () => {
        setIsAddWidgetsCollapsed(!isAddWidgetsCollapsed);
    };

    // Hilfsfunktion zum Generieren einer einzigartigen ID für jedes Widget
    function generateUniqueId() {
        return Math.random().toString(36).substr(2, 9);
    }

    useEffect(() => {
        // Reset position fix flag when page changes
        setPositionsAlreadyFixed(false);
        
        // Abonnement für Authentifizierungsstatus
        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            if (user) {
                console.log('🔍 PageSettings: Loading page data', { pageId, userId, spaceId, routePageName });
                
                // Bestimme Page-Referenz basierend auf Route-Typ
                let pageRef;
                let currentPageId;
                
                if (userId && spaceId && routePageName) {
                    // 3-Level Struktur: /settings/page/userId/spaceId/pageName
                    pageRef = ref(db, `/pages/${userId}/${spaceId}/${routePageName}`);
                    currentPageId = `${userId}/${spaceId}/${routePageName}`;
                    console.log('📄 Using 3-level structure:', currentPageId);
                } else if (userId && routePageName) {
                    // 2-Level Struktur: /settings/page/userId/pageName
                    pageRef = ref(db, `/pages/${userId}/${routePageName}`);
                    currentPageId = `${userId}/${routePageName}`;
                    console.log('📄 Using 2-level structure:', currentPageId);
                } else {
                    // Alte Struktur: /settings/page/pageId (Backward Compatibility)
                    pageRef = ref(db, `/pages/${pageId}`);
                    currentPageId = pageId;
                    console.log('📄 Using legacy structure:', currentPageId);
                }

                // Lade spezifische Page-Daten
                const unsubscribePageValue = onValue(pageRef, (snapshot) => {
                    const newPageData = snapshot.val();
                    if (newPageData) {
                        console.log('✅ Page data loaded:', newPageData);
                        
                        // Prüfen, ob die Widgets als Objekt vorliegen und in ein Array konvertieren
                        let widgetsArray = [];
                        if (newPageData.widgets && typeof newPageData.widgets === 'object') {
                            widgetsArray = Object.keys(newPageData.widgets).map(key => ({
                                id: key,
                                ...newPageData.widgets[key]
                            }));
                        }
                        
                        // ✅ AUTO-FIX: Repariere fehlende oder falsche position-Properties (nur beim initialen Load)
                        if (!positionsAlreadyFixed && widgetsArray.length > 0) {
                            const needsPositionFix = widgetsArray.some((w, idx) => 
                                w.position === undefined || w.position !== idx
                            );
                            
                            if (needsPositionFix) {
                                console.log('🔧 [POSITION-FIX] Initial repair of widget positions...');
                                widgetsArray = widgetsArray.map((widget, index) => ({
                                    ...widget,
                                    position: index
                                }));
                                
                                // Speichere die reparierten Positionen zurück in Firebase
                                const pageRefForFix = ref(db, `/pages/${currentPageId}`);
                                const fixedPageData = {
                                    ...newPageData,
                                    widgets: widgetsArray,
                                    updatedAt: new Date().toISOString()
                                };
                                set(pageRefForFix, fixedPageData).then(() => {
                                    console.log('✅ [POSITION-FIX] Widget positions repaired and saved to Firebase');
                                }).catch(error => {
                                    console.error('❌ [POSITION-FIX] Error saving repaired positions:', error);
                                });
                            }
                            
                            // Markiere als "fixed" damit es nicht wiederholt wird
                            setPositionsAlreadyFixed(true);
                        }

                        // ✅ Navigation Data laden (wenn vorhanden)
                        const navigationData = newPageData.navigation || {};
                        console.log('🧭 Navigation data loaded:', navigationData);

                        // 🚀 PERFORMANCE: Smart Comparison - nur updaten wenn sich WIRKLICH was geändert hat
                        setPageData(prevPageData => {
                            // Erste Ladung: Immer setzen
                            if (!prevPageData || !prevPageData.widgets) {
                                console.log('🆕 [PAGEDATA] Initial page load - setting data');
                                return { 
                                    ...newPageData, 
                                    widgets: widgetsArray,
                                    navigation: navigationData
                                };
                            }
                            
                            // Vergleiche Widgets (die sich am häufigsten ändern)
                            const widgetsChanged = JSON.stringify(prevPageData.widgets) !== JSON.stringify(widgetsArray);
                            const navigationChanged = JSON.stringify(prevPageData.navigation) !== JSON.stringify(navigationData);
                            
                            // Andere Page-Properties die sich ändern könnten
                            const otherPropsChanged = 
                                prevPageData.backgroundColor !== newPageData.backgroundColor ||
                                prevPageData.description !== newPageData.description ||
                                prevPageData.name !== newPageData.name ||
                                prevPageData.slug !== newPageData.slug;
                            
                            if (!widgetsChanged && !navigationChanged && !otherPropsChanged) {
                                console.log('⏭️ [PAGEDATA] No actual changes detected - skipping re-render');
                                return prevPageData; // ✅ Gleiche Referenz = kein Re-Render!
                            }
                            
                            console.log('🔄 [PAGEDATA] Changes detected - updating:', {
                                widgetsChanged,
                                navigationChanged,
                                otherPropsChanged
                            });
                            
                            return { 
                                ...newPageData, 
                                widgets: widgetsArray,
                                navigation: navigationData
                            };
                        });
                        
                        // ✅ Navigation State updaten (nur wenn nötig)
                        setIsNavigationEnabled(prev => {
                            const newEnabled = navigationData.enabled || false;
                            if (prev !== newEnabled) {
                                console.log('🔄 [NAV] Navigation enabled state changed:', newEnabled);
                                return newEnabled;
                            }
                            return prev;
                        });
                    } else {
                        console.log('❌ No page data found for:', currentPageId);
                    }
                    setLoading(false);
                });

                // Lade alle Pages für Navigation (3-Level + 2-Level Struktur)
                const allPagesRef = ref(db, `/pages/${user.uid}`);
                const unsubscribeAllPages = onValue(allPagesRef, (snapshot) => {
                    const pagesData = snapshot.val() || {};
                    const loadedPages = [];
                    
                    // Handle both 2-level and 3-level structures (same logic as PageNav)
                    Object.keys(pagesData).forEach(key => {
                        const pageOrSpace = pagesData[key];
                        
                        if (pageOrSpace.widgets) {
                            // 2-level: Direct page
                            loadedPages.push({
                                id: `${user.uid}/${key}`,
                                slug: key,
                                ...pageOrSpace
                            });
                        } else {
                            // 3-level: Space with pages
                            Object.keys(pageOrSpace).forEach(pageName => {
                                const pageData = pageOrSpace[pageName];
                                if (pageData.widgets) {
                                    loadedPages.push({
                                        id: `${user.uid}/${key}/${pageName}`,
                                        spaceId: key,
                                        slug: pageData.slug || pageName, // Use pageData.slug if available
                                        pageName: pageName, // Keep internal page name
                                        ...pageData
                                    });
                                }
                            });
                        }
                    });
                    
                    setAllPages(loadedPages);
                    console.log('📄 PageSettings: Loaded pages for navigation:', loadedPages);
                    console.log('📄 PageSettings: Current pageId for matching:', currentPageId);
                });

                // Bereinigungsfunktionen
                return () => {
                    unsubscribePageValue();
                    unsubscribeAllPages();
                };
            } else {
                // Benutzer ist nicht angemeldet
                setPageData(null);
                setLoading(false);
            }
        });

        // Bereinigungsfunktion für onAuthStateChanged-Abonnement
        return () => unsubscribeAuth();
    }, [auth, db, pageId, userId, spaceId, routePageName]);

    if (loading) {
        return <div className="loading-spinner">
            <h1>BonusClub</h1>
            <div className="spinner-circle"></div>
        </div>
    }

    if (!pageData) {
        return <div>Seite nicht gefunden.</div>; // Nachricht, wenn keine Daten vorhanden sind
    }

    // Zugriff auf die Daten
    const pageName = pageData.name || 'Unnamed Page';
    const pageDescription = pageData.description || 'No Description';
    const pageCustomRoute = pageData.customRoute || 'No Custom Route';

    // Handler for GlobalSettings Modal toggle
    const handleGlobalSettingsToggle = (isOpen) => {
        setIsGlobalSettingsOpen(isOpen);
    };

    // 🐛 DEBUG: Log allPages before passing to PageNav
    // console.log('🔍 [PageSettings RENDER] allPages length:', allPages.length);
    // console.log('🔍 [PageSettings RENDER] allPages first 3:', allPages.slice(0, 3).map(p => ({ id: p.id, slug: p.slug })));
    
    return (
        <div>
            <PageNav 
                pages={allPages} 
                projectName={pageData.name} 
                onGlobalSettingsToggle={handleGlobalSettingsToggle}
                onUndo={handleUndo}
                onRedo={handleRedo}
                canUndo={history.canUndo}
                canRedo={history.canRedo}
            />
            
            {/* 📱 DESKTOP/MOBILE VIEW TOGGLE */}
            <div className={`view-mode-toggle-container ${isGlobalSettingsOpen ? 'hidden' : ''}`}>
                <div className="view-mode-toggle">
                    <button 
                        className={`view-mode-btn ${!isMobileView ? 'active' : ''}`}
                        onClick={() => setIsMobileView(false)}
                        title="Desktop View"
                    >
                        <i className="fas fa-desktop"></i>
                        <span>Desktop</span>
                    </button>
                    <button 
                        className={`view-mode-btn ${isMobileView ? 'active' : ''}`}
                        onClick={() => setIsMobileView(true)}
                        title="Mobile View (380px)"
                    >
                        <i className="fas fa-mobile-alt"></i>
                        <span>Mobile</span>
                    </button>
                </div>
            </div>
            
            <div className="page-settings-container">
                <div className={`add-widgets ${isAddWidgetsCollapsed ? 'collapsed' : ''}`}>
                    {!isAddWidgetsCollapsed && (
                        <AddWidgets 
                            onAddWidget={handleAddWidget} 
                            products={products}
                            selectedProductId={selectedProductId}
                            onProductSelection={handleProductSelection}
                            spaceId={spaceId}
                            colors={colors}
                            fonts={spaceFonts}
                        />
                    )}
                    {isAddWidgetsCollapsed && (
                        <div className="add-widgets-collapsed-text">
                            Add Widgets
                        </div>
                    )}
                </div>
                <div 
                    className={`page-preview ${isMobileView ? 'mobile-view' : 'desktop-view'}`}
                    style={{
                        maxWidth: isMobileView ? '380px' : 'none',
                        margin: isMobileView ? '0 auto' : '0',
                        transition: 'all 0.3s ease'
                    }}
                >
                    {showPopup && (
                        <div className="page-preview-popup">New Widget Added
                            <img src="/img/down_2.svg" alt="" />
                        </div>
                    )}
                    <PagePreview
                        pageData={pageData}
                        onRemoveWidget={handleRemoveWidget}
                        onAddWidget={handleAddWidget}
                        onReorderWidgets={onReorderWidgets}
                        onToggleActivation={toggleWidgetActivation}
                        onUpdateWidget={handleUpdateWidget}
                        onDuplicateWidget={handleDuplicateWidget}
                        selectedWidget={selectedWidget}
                        pageId={routePageName || pageId}
                        spaceId={spaceId}
                        userId={userId}
                        isMobileView={isMobileView}
                        colors={colors}
                        colorAssignments={colorAssignments}
                        spaceFonts={spaceFonts}
                        isNavigationEnabled={isNavigationEnabled}
                        isWidgetEditCollapsed={isWidgetEditCollapsed}
                        isAddWidgetsCollapsed={isAddWidgetsCollapsed}
                        onToggleNavigation={(newState) => {
                            setIsNavigationEnabled(newState);
                            // Save to Firebase
                            const currentPageId = userId && spaceId && routePageName ? `${userId}/${spaceId}/${routePageName}` : 
                                                  userId && routePageName ? `${userId}/${routePageName}` : pageId;
                            const navigationRef = ref(db, `/pages/${currentPageId}/navigation`);
                            update(navigationRef, { enabled: newState });
                        }}
                    />
                </div>
                <div className={`widget-edit ${isWidgetEditCollapsed ? 'collapsed' : ''}`}>
                    {!isWidgetEditCollapsed && (
                        <>
                            {/* Tab Navigation */}
                            <div className="edit-tabs" style={{
                                marginBottom: '0px',
                                marginTop: '10px', 
                                width: '100%'
                            }}>
                                {(() => {
                                    const canShowPopup = canTriggerPopup();
        // Debug logs commented out for cleaner console
        // console.log('🔍 PageSettings: Rendering tabs, canShowPopup:', canShowPopup);
        // console.log('🔍 PageSettings: Selected widget:', selectedWidget?.type);
        // console.log('🔍 PageSettings: Products count:', products.length);
                                    
                                    return (
                                        <>
                                            <button 
                                                className={`edit-tab ${activeRightTab === 'widget' ? 'active' : ''}`}
                                                onClick={() => setActiveRightTab('widget')}
                                            >
                                                Widget Settings
                                            </button>
                                            
                                            
                                            {/* Popup Settings Tab - nur wenn Widget Popup triggern kann */}
                                            {canShowPopup && (
                                                <button 
                                                    className={`edit-tab ${activeRightTab === 'popup' ? 'active' : ''}`}
                                                    onClick={() => setActiveRightTab('popup')}
                                                >
                                                    <i className="fas fa-window-maximize"></i>
                                                    Popup Edit
                                                </button>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                            
                            {/* Tab Content */}
                            {activeRightTab === 'widget' && (
                                <WidgetEdit selectedWidget={selectedWidget} onUpdateWidget={handleUpdateWidget} colors={colors} pageData={pageData} />
                            )}
                            {activeRightTab === 'popup' && canTriggerPopup() && (
                                <PopupStylePreview 
                                    popupStyling={popupStyling}
                                    onStyleChange={setPopupStyling}
                                    products={products}
                                    selectedProductId={selectedWidget?.productId || ''}
                                    pageId={pageId}
                                />
                            )}
                        </>
                    )}
                    {isWidgetEditCollapsed && (
                        <div className="widget-edit-collapsed-text">
                            Edit Page
                        </div>
                    )}
                </div>
                
                {/* Toggle Button für linke Seite */}
                <button className="pages-toggle-icon-left" onClick={toggleAddWidgets} title={isAddWidgetsCollapsed ? "Panel öffnen" : "Panel schließen"}>
                        <svg 
                            className={`pages-toggle-arrow-left ${isAddWidgetsCollapsed ? 'collapsed' : ''}`}
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                        >
                            <polyline points="15,18 9,12 15,6"></polyline>
                        </svg>
                </button>

                {/* Toggle Button für rechte Seite */}
                <button className="pages-toggle-icon" onClick={toggleWidgetEdit} title={isWidgetEditCollapsed ? "Panel öffnen" : "Panel schließen"}>
                        <svg 
                            className={`pages-toggle-arrow ${isWidgetEditCollapsed ? 'collapsed' : ''}`}
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                        >
                            <polyline points="9,18 15,12 9,6"></polyline>
                        </svg>
                </button>
            </div>
            
            {/* Popup Manager für Product Form Popups */}
            <PopupManager 
                products={products}
                isEditMode={true}
                pageId={pageId}
                onSubmitForm={async (formData, productData) => {
                    console.log('Form submitted in edit mode (preview):', { formData, productData });
                    
                    // In edit mode, just show a preview message
                    alert(`✅ PREVIEW MODE\n\nThis would save customer data:\n\nName: ${formData.name}\nEmail: ${formData.email}\nProduct: ${productData.title}\n\nIn live mode, this data would be saved to your product's customer list.`);
                }}
                popupStyling={popupStyling}
            />
        </div>
    );
}

export default PageSettings;
